import { takeLatest, call, put, select, all, cancel, cancelled, spawn } from 'redux-saga/effects';
import _ from 'lodash';
import dayjs from 'dayjs';
import { parsePhoneNumber } from 'react-phone-number-input';
import analytics from '../../../utils/analytics';
import axios from '../axiosConfig';
import {
  bookTripRequest,
  bookTripSuccess,
  bookTripFailure,
  getTripConfirmationSuccess,
  changeValidation,
  changeTrip,
  getTripSuccess,
  setCruiseItineraryPaymentAmount,
  pollBookingRequest,
  getCustomerIdToSaveCard,
} from '../../slices/trip';
import { getSavedCardsRequest } from '../../slices/customer';
import { changeTourProviderAccount, setTourValidation } from '../../slices/tour';
import {
  BookingAction,
  CreditCardCodes,
  SearchStatus,
  SecurityInformationTypes,
  TravelTypes,
  TripConfirmationChanges,
  AgencyPlanningType,
  InsuranceMode,
  PaymentModes,
  MAX_CONFIRMATION_EMAILS,
  CreditCardsFormValidation,
} from '../../../constants';
import validateCheckoutForms from '../../../helpers/validationHelpers/validateCheckoutForms';
import getDuplicateCardMessage from '../../../helpers/validationHelpers/getDuplicateCardMessage';
import { handleCardTokenization } from '../../../helpers/tokenizeCards';
import { formatBookingError } from '../../../helpers/formatBookingError';
import { buildCreditCardPayments } from '../../../helpers/buildCreditCardPayments';
import getVGSProxyEndpoint from '../../../helpers/getVGSProxyEndpoint';
import getPriceBreakdownKey from '../../../helpers/getPriceBreakdownKey';
import formatPriceRequest from '../../../helpers/formatPriceRequest';
import getCabinPrice from '../../../helpers/getCabinPrice';
import buildBookingSuccessAmounts from '../../../helpers/buildBookingSuccessAmounts';
import getTripModes from '../../../helpers/getTripModes';
import buildTourBookingInput from '../../../helpers/buildTourBookingInput';
import validateEmail from '../../../helpers/validationHelpers/validateEmail';
import checkDoShowSavePassenger from '../../../helpers/checkDoShowSavePassenger';
import getEmergencyContactByPassenger from '../../../helpers/getEmergencyContactByPassenger';
import validateCustomerIdentifier from '../../../helpers/validationHelpers/validateCustomerIdentifier';
import validateRequestorIATA from '../../../helpers/validationHelpers/validateRequestorIATA';
import validatePromoCode from '../../../helpers/validationHelpers/validatePromoCode';
import checkDoChargeAgencyPlanningFees from '../../../helpers/selectors/checkDoChargeAgencyPlanningFees';
import getTripItineraries from '../../../helpers/getTripItineraries';
import checkDoShowMarkupInputs from '../../../helpers/selectors/checkDoShowMarkupInputs';
import getAcceptedPaymentCards from '../../../helpers/getAcceptedPaymentCards';
import formatGuestInfoForVerifyingTripConfirmationAccess from '../../../helpers/formatters/formatGuestInfoForVerifyingTripConfirmationAccess';
import {
  selectInsuranceInfo,
  clearInsuranceData,
  selectInsuranceTripInfo,
  selectInsuranceSaveAgentId,
  selectInsuranceHasNoArchRoamAccount,
  setTripInfoValidationErrors,
  setInsuranceOptionsFormValidations,
} from '../../slices/insurance';
import { selectArchRoamAgentId } from '../../slices/user';
import { getInsuranceTripInfoForBook } from '../../../helpers/InsuranceHelper';
import formatTravelMode from '../../../helpers/formatTravelMode';
import checkFlightItineraryPrivateFare from '../../../helpers/checkFlightItineraryPrivateFare';
import buildRadarSessionBySegment from './buildRadarSessionBySegment';
import getToursToBook from '../../../helpers/getToursToBook';

function* bookTripWorker({
  payload: {
    tripId,
    doReturnAvailabilityError,
    doReturnPriceChangeError,
    tripConfirmationChanges,
    doAcceptPriceQuoteChange,
    doConfirmClientOverwrite,
    trackingParams,
    tokenizeFunc,
    isFormValid,
  },
}) {
  let tokenizedCardMap = null;
  const tokenizedDataForValidation = tokenizeFunc && isFormValid ? { isTokenized: true } : null;
  let primaryPassengerInfo;
  // Hoisted so the catch block can attach hotel identity to the fail event.
  let hotelKeys = [];
  try {
    let { doChargeAgencyPlanningFees } = yield select((store) => store.trip);
    const timeZoneOffset = new Date().getTimezoneOffset();
    const {
      passengers,
      paymentMethods,
      specialBookingRequests,
      tripConfirmation,
      emptyPassengerDetailValidation,
      agencyPlanningFees,
      selectedAdditionalCheckoutFields,
      hasReviewedBookedTripPolicy,
      hasAgreedForDataCollection,
      hasReviewedBookingTripPolicy,
      isBookingReviewManual,
      agencyPlanningType,
      agencyPlanningFeesKeptByAgency,
      doSplitServiceFees,
      customerIdentifier,
      requestorIATA,
      travelAgencyAdvisorsEmail,
      promoCode,
      noteAirDeskRequest,
      userRemarks,
      chargeableItemOptions,
      hotelChainAdvisorBySegment,
      doSaveHotelChainAdvisorBySegment,
      markups,
    } = yield select((store) => store.trip);

    const { isLoggedIn } = yield select((store) => store.user);
    const {
      providerAccount: tourProviderAccount,
      tourTravelers,
      tourTravelerQuestions,
      tourOperatorNotes,
      tourHotelPickup,
    } = yield select((store) => store.tour);
    const currentTrip = yield select((store) => store.trip.selectedTrips[tripId]);
    const isTripQuote = currentTrip?.isTripQuote;

    const doChageFees = yield select(checkDoChargeAgencyPlanningFees(tripId));
    if (!doChageFees) {
      doChargeAgencyPlanningFees = false;
    }

    // Anonymous quote bookers must pay the planning fee the advisor put on the quote.
    // Check the quote itself, not the store value — the store can hold agency defaults
    // for fees the quote never carried (CRUIS-2645 regression).
    if (!isLoggedIn && isTripQuote && (currentTrip?.agencyPlanningFees ?? 0) >= 1) {
      doChargeAgencyPlanningFees = true;
    }

    const { priceBreakdowns } = yield select((store) => store.cruise);

    const trip = yield select((store) => store.trip.selectedTrips[tripId]);
    const insuranceInfo = yield select(selectInsuranceInfo({ tripId }));
    const insuranceAgentId = yield select(selectArchRoamAgentId);
    const insuranceHasNoArchRoamAccount = yield select(
      selectInsuranceHasNoArchRoamAccount({ tripId }),
    );
    const insuranceSaveAgentId = yield select(selectInsuranceSaveAgentId({ tripId }));
    const standaloneTripInfo =
      trip.insuranceItinerary?.insuranceMode === InsuranceMode.Standalone
        ? yield select(selectInsuranceTripInfo({ tripId }))
        : null;
    const insuranceTripInfo = getInsuranceTripInfoForBook(trip, standaloneTripInfo);

    const {
      tripCode,
      cruiseItinerary,
      cruisePayments,
      doChangeItineraryInBookedTrip,
      itinerariesOnHold,
      addPaymentMethodToTravelTypes,
      hotelItineraries,
      brochureId,
      isDedicatedHotelTrip,
    } = trip;

    hotelKeys = (
      Array.isArray(hotelItineraries) ? hotelItineraries : Object.values(hotelItineraries || {})
    )
      .map((h) => h?.hotelKey)
      .filter(Boolean);

    let url = '/trips/book';
    if (tripConfirmationChanges && tripConfirmationChanges.hasChanges) {
      url = '/trips/updateBooking';
    } else if (isTripQuote) {
      url = '/trips/book/tripquote';
    } else if (brochureId) {
      url = '/trips/book/brochure';
    } else if (isDedicatedHotelTrip) {
      url = '/trips/book/dedicatedHotel';
    }

    const { availableStates, travelAgency, email } = yield select((store) => store.user);

    const loggedInUserEmail = email && email.length ? email : '';

    const targetCards = Object.values(paymentMethods).filter(
      (card) =>
        card.isPicked &&
        (card.selectedCard ||
          card.paymentMode === PaymentModes.CASH ||
          (card.creditCard &&
            card.creditCard.expiration &&
            card.creditCard.cardNumber &&
            CreditCardCodes[card.creditCard.component])),
    );
    const { itinerariesToBookWithPaymentMethod, itinerariesToBook } = getTripModes(
      trip,
      travelAgency,
    );

    let isBooking = tripConfirmationChanges
      ? tripConfirmationChanges.doAddPaymentMethod
      : itinerariesToBookWithPaymentMethod.length > 0 ||
        (doChargeAgencyPlanningFees && !trip.itinerariesOnHold?.length);

    if (isBooking && !isLoggedIn && (travelAgency || {}).useCardFromAgencyProfile) {
      isBooking = false;
    }
    const doHoldOnlyCruiseAsOption =
      !tripConfirmation &&
      (itinerariesOnHold || []).length === 1 &&
      (itinerariesOnHold[0] || {}).itineraryMode === TravelTypes.cruise;

    // An update from the confirmation page changes cruise options or adds a card and books no tour, so the
    // tour inputs are neither validated nor sent; a failed tour would otherwise block it after a reload.
    const toursToBook = tripConfirmationChanges ? [] : getToursToBook(trip, tripConfirmation);

    // cleaned paymentMethods object with only valid selectedFor items
    const values = chargeableItemOptions.map((o) => o.value);
    const updatedCreditCards = Object.fromEntries(
      Object.entries(paymentMethods).map(([key, card]) => [
        key,
        {
          ...card,
          selectedFor:
            card.selectedFor && card.selectedFor.length
              ? card.selectedFor.filter((v) => values.includes(v))
              : undefined,
        },
      ]),
    );

    const hasHotels = hotelItineraries && Object.keys(hotelItineraries).length > 0;
    const acceptedCards = hasHotels
      ? getAcceptedPaymentCards(
          Array.isArray(hotelItineraries) ? hotelItineraries : Object.values(hotelItineraries),
        )
      : {};
    const [
      isValidForms,
      newPassengersFormsValidation,
      creditCardsFormValidations,
      similarPassengersValidation,
      _emptyPassengerDetailValidation,
      isBookedTripPolicyReviewValidated,
      isBookingTripPolicyReviewValidated,
      isSavedCardValidated,
      isAgencyPlanningFeesValid,
      invalidAdditionalCheckoutFields,
      isNoteAirDeskValidated,
      isAgreedForDataCollection,
      isUserRemarksValidated,
      invalidHotelSpecialRequestsBySegmentId = {},
      invalidTourTravelersBySegmentId = {},
      invalidTourQuestionsBySegmentId = {},
      invalidTourPickupBySegmentId = {},
      insuranceTripInfoFormValidations,
      insuranceOptionsFormValidations,
      validationErrorSummary,
      tourProviderAccountError,
    ] = validateCheckoutForms(
      trip,
      passengers,
      updatedCreditCards,
      availableStates,
      isBooking,
      travelAgency,
      tripConfirmation,
      !!(emptyPassengerDetailValidation || {}).doIgnore,
      doChargeAgencyPlanningFees,
      agencyPlanningFees,
      hasReviewedBookingTripPolicy,
      doChangeItineraryInBookedTrip,
      hasReviewedBookedTripPolicy,
      isLoggedIn,
      doHoldOnlyCruiseAsOption,
      itinerariesToBook,
      doSplitServiceFees,
      agencyPlanningFeesKeptByAgency,
      acceptedCards,
      isBookingReviewManual,
      noteAirDeskRequest,
      hasAgreedForDataCollection,
      selectedAdditionalCheckoutFields,
      userRemarks,
      chargeableItemOptions,
      tokenizedDataForValidation,
      loggedInUserEmail,
      hotelChainAdvisorBySegment,
      specialBookingRequests,
      insuranceInfo,
      insuranceTripInfo,
      tourTravelers,
      tourTravelerQuestions,
      tourHotelPickup,
      tourProviderAccount,
      toursToBook,
    );

    if (!isValidForms) {
      yield spawn([analytics, 'track'], 'bookTripWorker.bookTrip.validationFailed', {
        tripId,
        ...(validationErrorSummary || {}),
        isBookedTripPolicyReviewValidated,
        isBookingTripPolicyReviewValidated,
        isSavedCardValidated,
        isAgencyPlanningFeesValid,
        isAgreedForDataCollection,
        isNoteAirDeskValidated,
        isUserRemarksValidated,
      });
      if (!_.isEmpty(_emptyPassengerDetailValidation || {})) {
        yield put(
          changeValidation({
            emptyPassengerDetailValidation: _emptyPassengerDetailValidation,
          }),
        );
      } else {
        const effects = newPassengersFormsValidation.map((formValidations, targetIndex) =>
          put(
            changeValidation({
              field: 'passengers',
              targetIndex,
              formValidations,
              similarPassengersValidation,
              isSavedCardValidated,
              isAgencyPlanningFeesValid,
              invalidAdditionalCheckoutFields,
            }),
          ),
        );
        yield all(effects);
        yield put(
          changeValidation({
            creditCardsValidations: creditCardsFormValidations,
            similarPassengersValidation,
            isBookedTripPolicyReviewValidated,
            isBookingTripPolicyReviewValidated,
            isNoteAirDeskValidated,
            isSavedCardValidated,
            isAgencyPlanningFeesValid,
            invalidAdditionalCheckoutFields,
            isAgreedForDataCollection,
            isUserRemarksValidated,
            invalidHotelSpecialRequestsBySegmentId,
          }),
        );
        yield put(
          setTourValidation({
            invalidTourTravelersBySegmentId,
            invalidTourQuestionsBySegmentId,
            invalidTourPickupBySegmentId,
          }),
        );
        yield put(changeTourProviderAccount({ error: tourProviderAccountError || null }));
        yield put(
          setTripInfoValidationErrors({ tripId, errors: insuranceTripInfoFormValidations }),
        );
        yield put(
          setInsuranceOptionsFormValidations({ tripId, errors: insuranceOptionsFormValidations }),
        );
      }
      yield cancel();
    }

    if (
      (tripConfirmationChanges &&
        tripConfirmationChanges.doAddPaymentMethod &&
        addPaymentMethodToTravelTypes.length === 0) ||
      validateCustomerIdentifier(customerIdentifier) ||
      validateRequestorIATA(requestorIATA)
    ) {
      yield spawn([analytics, 'track'], 'bookTripWorker.bookTrip.guardFailed', {
        tripId,
        reason: 'paymentMethod_or_customerIdentifier_or_requestorIATA',
        hasNoPaymentMethodTypes:
          tripConfirmationChanges?.doAddPaymentMethod && addPaymentMethodToTravelTypes.length === 0,
        isCustomerIdentifierInvalid: validateCustomerIdentifier(customerIdentifier),
        isRequestorIATAInvalid: validateRequestorIATA(requestorIATA),
      });
      yield cancel();
    }
    if (validatePromoCode(promoCode)) {
      yield spawn([analytics, 'track'], 'bookTripWorker.bookTrip.guardFailed', {
        tripId,
        reason: 'invalidPromoCode',
      });
      yield cancel();
    }

    // Handle tokenization
    yield call([analytics, 'track'], 'bookTripWorker.bookTrip.tokenizeCard.start', {
      tripId,
      hasTokenizeFunc: !!tokenizeFunc,
      isFormValid,
    });
    try {
      tokenizedCardMap = Object.values(paymentMethods).some(
        (p) => p.paymentMode === PaymentModes.NEW_CREDIT_CARD,
      )
        ? yield call(handleCardTokenization, tokenizeFunc, isFormValid)
        : null;
    } catch (error) {
      const tokenizeErrorMessage = (error && error.message) || '';
      const tokenizeErrorCode = /vgs/i.test(tokenizeErrorMessage)
        ? 'vgs_unavailable'
        : /network|timeout|econn|fetch/i.test(tokenizeErrorMessage)
          ? 'network'
          : /invalid\s+card|card\s+(number|cvc|cvv|expir)|tokeniz/i.test(tokenizeErrorMessage)
            ? 'invalid_card'
            : 'unknown';

      yield spawn([analytics, 'track'], 'bookTripWorker.bookTrip.tokenizeCard.fail', {
        tripId,
        errorCode: tokenizeErrorCode,
        error: JSON.stringify(tokenizeErrorMessage),
      });
      yield put(bookTripFailure({ error: error.message }));
      yield cancel();
    }

    // The same card entered twice tokenizes to the same token; the backend rejects that as a duplicate.
    // Every occurrence after the first is flagged so the error shows on the card the user re-entered.
    // A hold sends no cards (doIncludeCC), so duplicates only matter when booking.
    const seenCardTokens = new Set();
    const duplicateCardEntries = Object.entries((isBooking && tokenizedCardMap) || {}).filter(
      ([index, tokenPayload]) => {
        const cardToken = tokenPayload?.cardNumber;
        if (!paymentMethods[index]?.isPicked || !cardToken) return false;
        if (seenCardTokens.has(cardToken)) return true;
        seenCardTokens.add(cardToken);
        return false;
      },
    );
    const duplicateCardIndexes = duplicateCardEntries.map(([index]) => String(index));
    // Kept apart from cardNumber, which the manual validator also fills for VGS cards.
    const duplicateCardValidations = Object.entries(paymentMethods)
      .filter(
        ([index, pm]) =>
          duplicateCardIndexes.includes(index) || pm?.formValidations?.duplicateCardNumber,
      )
      .map(([index, pm]) => {
        const duplicateEntry = duplicateCardEntries.find(
          ([duplicateIndex]) => String(duplicateIndex) === index,
        );
        return {
          cardIndex: Number(index),
          cardformValidations: {
            ...CreditCardsFormValidation,
            ...(pm?.formValidations || {}),
            duplicateCardNumber: duplicateEntry
              ? getDuplicateCardMessage(duplicateEntry[1]?.cardNumberLast4Chars)
              : null,
          },
        };
      });
    if (duplicateCardValidations.length) {
      yield put(changeValidation({ creditCardsValidations: duplicateCardValidations }));
    }
    if (duplicateCardEntries.length) {
      yield spawn([analytics, 'track'], 'bookTripWorker.bookTrip.validationFailed', {
        tripId,
        failedCategories: ['payment'],
        primaryFailedCategory: 'payment',
        errorCodes: ['payment.duplicateCard'],
        errorCount: duplicateCardEntries.length,
      });
      yield put(
        bookTripFailure({
          error: getDuplicateCardMessage(duplicateCardEntries[0][1]?.cardNumberLast4Chars),
        }),
      );
      yield cancel();
    }

    const doIncludeCC =
      isBooking &&
      (targetCards.length > 0 || (tokenizedCardMap && Object.keys(tokenizedCardMap).length > 0));
    let cruiseTotalAmounts = null;
    if (cruiseItinerary) {
      // To recheck price before book
      const pricingKey = getPriceBreakdownKey(
        formatPriceRequest({
          itinerary: trip.cruiseItinerary,
          passengers,
          bookingResponses: tripConfirmation && tripConfirmation.bookingResponses,
        }),
      );
      if (priceBreakdowns[pricingKey]) {
        cruiseTotalAmounts = _.map(priceBreakdowns[pricingKey], (pricing) =>
          getCabinPrice(pricing),
        );
      }
    }

    const tripChanges = {};
    if (
      tripConfirmationChanges &&
      tripConfirmationChanges.doAddPaymentMethod &&
      addPaymentMethodToTravelTypes &&
      addPaymentMethodToTravelTypes.length
    ) {
      _.each(addPaymentMethodToTravelTypes, (mode) => {
        tripChanges[mode] = [TripConfirmationChanges.Payment];
      });
    }
    if (tripConfirmationChanges && tripConfirmationChanges.hasChanges) {
      tripChanges[TravelTypes.cruise] = tripChanges[TravelTypes.cruise] || [];
      if ((tripConfirmationChanges.packageChanges || []).length > 0) {
        tripChanges[TravelTypes.cruise].push(TripConfirmationChanges.Packages);
      }
      if ((tripConfirmationChanges.servicesChanges || []).length > 0) {
        tripChanges[TravelTypes.cruise].push(TripConfirmationChanges.SpecialServices);
      }
      if ((tripConfirmationChanges.insuranceChanges || []).length > 0) {
        tripChanges[TravelTypes.cruise].push(TripConfirmationChanges.Insurance);
      }
      if ((tripConfirmationChanges.cruiseTransportChanges || []).length > 0) {
        tripChanges[TravelTypes.cruise].push(TripConfirmationChanges.CruiseTransport);
      }
      if ((tripConfirmationChanges.diningChanges || []).length > 0) {
        tripChanges[TravelTypes.cruise].push(TripConfirmationChanges.Dining);
      }
      if (!tripChanges[TravelTypes.cruise].length) tripChanges[TravelTypes.cruise] = undefined;
    }
    const passengersByRoom = _.groupBy(Object.values(passengers), (p) => p.roomIndex);

    const sessionKey =
      cruiseItinerary &&
      cruiseItinerary.cabins &&
      cruiseItinerary.cabins[0] &&
      cruiseItinerary.cabins[0].selectedShipCabinCategory
        ? cruiseItinerary.cabins[0].selectedShipCabinCategory.sessionKey
        : undefined;

    const insuranceRequest = insuranceInfo
      ? {
          insuranceInfo,
          tripInfo: {
            startDate: dayjs(insuranceTripInfo.startDate).format('YYYY-MM-DD'),
            endDate: dayjs(insuranceTripInfo.endDate).format('YYYY-MM-DD'),
            depositDate: dayjs(insuranceTripInfo.depositDate).format('YYYY-MM-DD'),
            tripCost: insuranceTripInfo.tripCost,
            destinations: insuranceTripInfo.destinations,
          },
          agentId: !insuranceHasNoArchRoamAccount && insuranceAgentId ? insuranceAgentId : null,
          doSaveAgentId: insuranceSaveAgentId,
        }
      : null;

    const doShowMarkup = yield select(checkDoShowMarkupInputs(tripId));
    if (doShowMarkup) {
      const tripItineraries = getTripItineraries(trip);
      for (const tripSegmentId in markups[tripId]) {
        const markupAmount = markups[tripId][tripSegmentId];
        const itinerary = tripItineraries.find(
          (it) => it.tripSegmentId.toString() === tripSegmentId,
        );
        if (itinerary && markupAmount > itinerary.cost.price) {
          yield spawn([analytics, 'track'], 'bookTripWorker.bookTrip.validationFailed', {
            tripId,
            failedCategories: ['markup'],
            primaryFailedCategory: 'markup',
            errorCodes: ['markup.exceedsTotal'],
            errorCount: 1,
            travelMode: itinerary.mode,
          });
          yield put(
            bookTripFailure({
              error: `Markup amount for ${formatTravelMode(itinerary.mode)} cannot exceed the total cost.`,
            }),
          );
          yield cancel();
        }
        if (
          itinerary.mode === TravelTypes.flight &&
          checkFlightItineraryPrivateFare(itinerary) &&
          (!markupAmount || markupAmount <= 0)
        ) {
          yield spawn([analytics, 'track'], 'bookTripWorker.bookTrip.validationFailed', {
            tripId,
            failedCategories: ['markup'],
            primaryFailedCategory: 'markup',
            errorCodes: ['markup.missingPrivateFare'],
            errorCount: 1,
            travelMode: itinerary.mode,
            hasPrivateFare: true,
          });
          yield put(
            bookTripFailure({
              error: `Markup is required for private fare flights.`,
            }),
          );
          yield cancel();
        }
      }
    }

    const radarSessionBySegment = yield call(buildRadarSessionBySegment, {
      toursToBook,
      tripId,
    });

    const tourBookingInputBySegment = toursToBook.reduce(
      (inputs, tour) => ({
        ...inputs,
        [tour.tripSegmentId]: buildTourBookingInput({
          segmentId: tour.tripSegmentId,
          tourTravelers,
          tourTravelerQuestions,
          tourOperatorNotes,
          tourHotelPickup,
        }),
      }),
      {},
    );

    const config = {
      method: 'post',
      url:
        (doIncludeCC && !(tokenizedCardMap && Object.keys(tokenizedCardMap).length > 0)
          ? getVGSProxyEndpoint()
          : '') + url,
      data: JSON.stringify({
        tripId: tripCode,
        diningInputs: cruiseItinerary
          ? _.map(cruiseItinerary.cabins, (cabin, roomIndex) => ({
              roomIndex,
              selectedDiningSeat: cabin.selectedDiningSeat,
              selectedDiningTable: cabin.selectedDiningTable,
            }))
          : undefined,
        cruiseTotalAmounts: cruiseTotalAmounts || undefined,
        selectedDiningTable: cruiseItinerary ? cruiseItinerary.selectedDiningTable : undefined,
        loggedInUserEmail,
        specialRequests: hasHotels ? specialBookingRequests || {} : undefined,
        tourBookingInputBySegment: toursToBook.length > 0 ? tourBookingInputBySegment : undefined,
        radarSessionBySegment: toursToBook.length > 0 ? radarSessionBySegment : undefined,
        tourProviderToken:
          toursToBook.length > 0 ? tourProviderAccount.token || undefined : undefined,
        doBookToursAsAgency:
          toursToBook.length > 0 && tourProviderAccount.doBookAsAgency ? true : undefined,
        hotelChainAdvisorBySegment: hasHotels ? hotelChainAdvisorBySegment || {} : undefined,
        doSaveHotelChainAdvisorBySegment: hasHotels
          ? doSaveHotelChainAdvisorBySegment || {}
          : undefined,
        noteToAirDesk:
          isBookingReviewManual && noteAirDeskRequest && noteAirDeskRequest.length
            ? noteAirDeskRequest.trim()
            : undefined,
        doReturnAvailabilityError:
          doReturnAvailabilityError === true ? doReturnAvailabilityError : undefined,
        doReturnPriceChangeError:
          doReturnPriceChangeError === true ? doReturnPriceChangeError : undefined,
        trackingParams:
          trackingParams && Object.keys(trackingParams).length ? trackingParams : undefined,
        passengers: Object.values(passengers).map((item, index) => {
          let doSaveCustomerInfo = validateEmail(item.contactEmail);
          if (doSaveCustomerInfo) {
            doSaveCustomerInfo =
              checkDoShowSavePassenger(
                Object.values(passengers),
                item,
                passengersByRoom[item.roomIndex].findIndex(
                  (c) => c.passengerIndex === item.passengerIndex,
                ),
                trip.isUpdatingBookedTrip,
                isLoggedIn,
                travelAgency,
                loggedInUserEmail,
              ) && item.doSaveCustomerInfo;
          }
          const advancePassengerInformations = _.filter(
            item.travelerPreferences.advancePassengerInformations || [],
            (d) => d.apisType !== SecurityInformationTypes.SELECT_APIS_TYPE,
          );

          const passenger = {
            ...item,
            title: trip.cruiseItinerary ? item.title : undefined,
            phone: item.phone,
            phoneWithoutCountryCode: parsePhoneNumber(item?.phone ?? '')?.nationalNumber ?? '',
            pastPassengerNumber: item.isPastPassenger ? item.pastPassengerNumber : undefined,
            birthDate: item.birthDate ? dayjs(item.birthDate).format('YYYY-MM-DD') : undefined,
            id: index,
            doSaveCustomerInfo,
            additionalConfirmationEmails: item.doSendConfirmationEmail
              ? _.uniq(
                  (item.additionalConfirmationEmails || [])
                    .map((emailAddress) => (emailAddress || '').trim())
                    .filter((emailAddress) => emailAddress && validateEmail(emailAddress)),
                ).slice(0, MAX_CONFIRMATION_EMAILS - 1)
              : [],
            travelerPreferences: {
              ...item.travelerPreferences,
              hotelLoyaltyNumbers: _.pickBy(
                item.travelerPreferences.hotelLoyaltyNumbers || {},
                (value, key) => !!key && String(value || '').trim() !== '',
              ),
              flightMealPreference: item.travelerPreferences.flightMealPreference
                ? item.travelerPreferences.flightMealPreference.value
                : undefined,
              flightSeatPreference: item.travelerPreferences.flightSeatPreference
                ? item.travelerPreferences.flightSeatPreference.value
                : undefined,
              flightSpecialAssistance: item.travelerPreferences.flightSpecialAssistance
                ? item.travelerPreferences.flightSpecialAssistance.value
                : undefined,
              frequentFlyerNumbers:
                item.travelerPreferences.frequentFlyerNumbers &&
                item.travelerPreferences.frequentFlyerNumbers.length
                  ? item.travelerPreferences.frequentFlyerNumbers
                      .filter((p) => p.selectedValue && p.selectedValue.value && p.value)
                      .reduce((acc, val) => {
                        acc[val.selectedValue.value] = val.value;
                        return acc;
                      }, {})
                  : undefined,
              emergencyContact: getEmergencyContactByPassenger(item, passengers),
              selectedVirtuosoCruiseBenefit:
                item.travelerPreferences.selectedVirtuosoCruiseBenefit > 0
                  ? item.travelerPreferences.selectedVirtuosoCruiseBenefit
                  : undefined,
              advancePassengerInformations: advancePassengerInformations.length
                ? advancePassengerInformations.map((p) => ({
                    ...p,
                    expirationDate: p.expirationDate
                      ? dayjs(p.expirationDate).format('YYYY-MM-DD')
                      : undefined,
                    visaIssueDate: p.visaIssueDate
                      ? dayjs(p.visaIssueDate).format('YYYY-MM-DD')
                      : undefined,
                  }))
                : undefined,
            },
          };

          delete passenger.formValidations;
          if (!passenger.passengerOccupation) delete passenger.passengerOccupation;
          if (!passenger.insuranceCoverage) delete passenger.insuranceCoverage;
          if (!passenger.phoneType) delete passenger.phoneType;
          delete passenger.isPastPassenger;
          return passenger;
        }),
        paymentMethods: buildCreditCardPayments({
          doIncludeCC,
          paymentMethods: updatedCreditCards,
          tokenizedCardMap,
          isLoggedIn,
        }),
        agencyPlanningFees: doChargeAgencyPlanningFees ? agencyPlanningFees : undefined,
        agencyPlanningType: doChargeAgencyPlanningFees
          ? agencyPlanningType || AgencyPlanningType.AgencyPlanningFeesPerTrip
          : undefined,
        agencyPlanningFeesKeptByAgency:
          doChargeAgencyPlanningFees && doSplitServiceFees
            ? agencyPlanningFeesKeptByAgency
            : undefined,
        cruisePayments:
          isBooking && cruisePayments && Object.values(cruisePayments).length
            ? _.filter(Object.values(cruisePayments), (c) => c !== null && c !== undefined)
            : undefined,
        itinerariesOnHold,
        tripChanges: tripChanges && Object.keys(tripChanges).length ? tripChanges : undefined,
        customerIdentifier: !validateCustomerIdentifier(customerIdentifier)
          ? customerIdentifier
          : undefined,
        requestorIATA: !validateRequestorIATA(requestorIATA) ? requestorIATA : undefined,
        promoCode:
          promoCode && promoCode.length && !validatePromoCode(promoCode) ? promoCode : undefined,
        travelAgencyAdvisorsEmail:
          trip?.advisorEmail ||
          (travelAgencyAdvisorsEmail && travelAgencyAdvisorsEmail.length
            ? travelAgencyAdvisorsEmail
            : undefined),

        timeZoneOffset,
        doAcceptPriceQuoteChange,
        doConfirmClientOverwrite: doConfirmClientOverwrite || undefined,
        sessionKey,
        markups:
          doShowMarkup && markups[tripId] && Object.values(markups[tripId]).every((m) => m > 0)
            ? markups[tripId]
            : undefined,
        userRemarks:
          userRemarks && userRemarks.length
            ? userRemarks.map((remark) => ({
                ...remark,
                code: remark.code && remark.code.length ? remark.code : undefined,
                segment: undefined,
                id: undefined,
                segments:
                  remark.segment && remark.segment.length
                    ? _.map(remark.segment.split(','), (s) => +s)
                    : undefined,
              }))
            : undefined,
        insurance: insuranceRequest,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    };
    primaryPassengerInfo = formatGuestInfoForVerifyingTripConfirmationAccess(
      passengers[0]?.firstName,
      passengers[0]?.lastName,
      passengers[0]?.contactEmail,
    );
    yield call([analytics, 'track'], 'bookTripWorker.bookTrip.sending', {
      tripId,
      url: config.url,
      isBooking,
      doIncludeCC,
      hasTokenizedCards: !!(tokenizedCardMap && Object.keys(tokenizedCardMap).length > 0),
      itinerariesToBookCount: itinerariesToBook.length,
      passengersCount: Object.values(passengers).length,
    });
    const { data } = yield call(axios, config);
    if (data?.status === SearchStatus.SEARCHING) {
      yield put(
        pollBookingRequest({
          tripId,
          form: primaryPassengerInfo,
        }),
      );
    } else if (
      tripConfirmationChanges &&
      tripConfirmationChanges.doAddPaymentMethod &&
      data &&
      data.tripStatus !== BookingAction.BOOK
    ) {
      yield put(bookTripFailure('Failed to confirm held cruise booking, please contact support!'));
    } else {
      const bookingAmounts = buildBookingSuccessAmounts({
        trip,
        priceBreakdowns,
        passengers,
        bookingResponses: tripConfirmation && tripConfirmation.bookingResponses,
      });
      yield call([analytics, 'track'], 'bookTripWorker.bookTrip.apiSuccess', {
        tripId,
        ...bookingAmounts,
        ...(brochureId ? { brochureId, bookingSource: 'brochure' } : { bookingSource: 'direct' }),
        ...(hotelKeys.length ? { hotelKey: hotelKeys[0], hotelKeys } : {}),
      });
      yield put(
        bookTripSuccess({
          data,
        }),
      );
      yield put(clearInsuranceData({ tripId }));
      // After a successful booking, check if a card was saved and if the saved card list
      // has already been loaded into the state, we need to refresh it to avoid showing stale data.
      const didRequestToSaveCard = Object.values(paymentMethods).some(
        (card) => card?.creditCard?.doSaveCard,
      );
      if (didRequestToSaveCard) {
        const { getSavedCardsStatuses } = yield select((store) => store.customer);
        const customerId = yield select(getCustomerIdToSaveCard);
        const areCardsAlreadyLoaded = getSavedCardsStatuses && getSavedCardsStatuses[customerId];
        if (areCardsAlreadyLoaded) {
          yield put(
            getSavedCardsRequest({
              customerId,
              withAddressOnly: trip.isCardAddressRequired,
            }),
          );
        }
      }

      yield put(
        changeTrip({
          field: 'userRemarks',
          value: [],
        }),
      );
    }

    if (data && data.tripId) {
      yield put(
        getTripConfirmationSuccess({
          tripConfirmation: data,
          travelAgency,
        }),
      );
      yield put(
        setCruiseItineraryPaymentAmount({
          tripConfirmation: data,
        }),
      );
      yield put(
        changeValidation({
          emptyPassengerDetailValidation: {},
        }),
      );
    }
  } catch (error) {
    console.error(error);
    const formattedError = formatBookingError(error);
    yield call([analytics, 'track'], 'bookTripWorker.bookTrip.fail', {
      tripId,
      error: formattedError?.error,
      ...(hotelKeys.length ? { hotelKey: hotelKeys[0], hotelKeys } : {}),
    });

    if (formattedError.shouldPoll) {
      yield put(pollBookingRequest({ tripId, form: primaryPassengerInfo }));
      return;
    }

    if (formattedError.tripData) {
      yield put(
        getTripSuccess({
          tripId,
          ...formattedError.tripData,
        }),
      );
    }

    yield put(
      bookTripFailure({
        error: formattedError.error,
        isTripAvailabilityPriceChanged: formattedError.isTripAvailabilityPriceChanged,
        tripAvailabilityPriceChangeResponse: formattedError.tripAvailabilityPriceChangeResponse,
        clientOverwriteWarning: formattedError.clientOverwriteWarning,
      }),
    );
  } finally {
    if (yield cancelled()) {
      yield spawn([analytics, 'track'], 'bookTripWorker.bookTrip.cancelled', { tripId });
      yield put(changeTrip({ field: 'tripBookingStatus', value: SearchStatus.DONE }));
    }
  }
}

function* tripBookWatcher() {
  yield takeLatest(bookTripRequest.toString(), bookTripWorker);
}

export default tripBookWatcher;
